<script>
  import { onMount, onDestroy, afterUpdate } from "svelte";
  import Section, { blurSections } from "./Section.svelte";
  import { componentStructure, fixMissingProperties } from "./component-config";
  import { array_move, getStyleValue } from "../Common/tools";
  import {External, M} from "../external";

  export let data, meta, options = {}, onChange;
  export let onFocus,
    onBlur,
    isFixed = false;

  let sortable,
    editorWrapper,
    jSortable,
    blurElement,
    alignment = "auto",
    isEmpty = false;

  let blur = () => {
    blurSections();
    onBlur();
  };

  let action = (id, action) => {
    if (isFixed) {
      return;
    }

    if (action === "up" && id !== 0) {
      onBlur({
        action,
        from: id,
        to: id - 1
      });
    } else if (action === "down" && data.content.length - 1 > id) {
      onBlur({
        action,
        from: id,
        to: id + 1
      });
    } else if (action === "clone" || action === "remove") {
      onBlur({
        index: id,
        action,
        content: JSON.stringify(data.content[id])
      });
    } else {
      onBlur();
    }

    switch (action) {
      case "up":
        if (id !== 0) {
          data.content = array_move(data.content, id, id - 1);
        }
        break;
      case "down":
        if (data.content.length - 1 > id) {
          data.content = array_move(data.content, id, id + 1);
        }
        break;
      case "clone":
        let elem = data.content[id] || {};
        const item = JSON.parse(JSON.stringify(elem)); // we need a deep copy here!
        const itemId = data.content.push(item);
        data.content = array_move(data.content, itemId - 1, id + 1);
        break;
      case "remove":
        data.content.splice(id, 1);
        data = data;
        break;
    }
  };

  afterUpdate(function () {
    const editorWrapper = External.$(".wrapper");
    const be = External.$(blurElement);

    setTimeout(function () {
      be.height(editorWrapper.height());
    }, 500);
  });

  onMount(function () {
    jSortable = External.$(sortable);
    jSortable.sortable({
      items: "table.focus-element",
      placeholder: "ui-state-highlight",
      handle: ".invalid-handle",
      forcePlaceholderSize: true,
      stop: function (ev, ui) {
        let idx = isEmpty ? 0 : ui.item.index();
        ui.item.remove();
        data.content.unshift(componentStructure(ui.item.context.id, options.features || {}));
        data.content = array_move(data.content, 0, idx);
        onChange(idx, "add");
      },
      start: function (ev, ui) {
        ui.helper
          .css({
            width: 150,
            height: 1
          })
          .find("img")
          .width(150);
      }
    });

    let jqEditorWrapper = External.$(editorWrapper);
    jqEditorWrapper.on(
      "scroll.editor",
      External.$.throttle(250, function () {
        let toolbars = jqEditorWrapper.find(".note-toolbar-visible");
        toolbars.each(function (idx, elem) {
          External.Tools.fixTextEditorToolbarPosition(External.$(elem), false);
        });
      })
    );

    let globalColorsCSS = "";

    for (let key in External.Website.globalStyling.colors) {
      let value = External.Website.globalStyling.colors[key];
      globalColorsCSS += ".color-" + key + " { color: " + value + "; } ";
      globalColorsCSS += ".bg-color-" + key + " { background-color: " + value + "; } ";
      globalColorsCSS += ".border-color-" + key + " { border-color: " + value + "; } ";
      globalColorsCSS += ".fill-color-" + key + " { fill: " + value + "; } ";
    }
    let style = document.createElement("style");
    style.id = "emails-global-colors";
    style.innerHTML = globalColorsCSS;
    editorWrapper.appendChild(style);
    fixMissingProperties(data.content);
  });

  onDestroy(function () {
    External.$(editorWrapper).off("scroll.editor");
    jSortable.sortable("destroy");
  });

  $: {
    alignment = data.style.align === "left" ? "flex-start" : data.style.align === "right" ? "flex-end" : "auto";
    isEmpty = data.content.length === 0 || (data.content.length === 1 && data.content[0].type === "unsubscribe");
  }
</script>

<div class="flex-1 relative" style="overflow-y:auto; background-color:{data.style.backgroundColor};{options.isContentSplitTest ? 'margin-top: 20px;' : ''}">
  <div bind:this={editorWrapper} class="editor-wrapper relative">
    <div class="table-wrapper" style="background-color: {data.style.backgroundColor}; align-self: {alignment};">
      <table
        class="wrapper"
        align="center"
        cellpadding="0"
        cellspacing="0"
        width={data.style.width || "100%"}
        style="font-family: '{data.style.fontFamily}'; min-width: {data.style.width}; left: 50%;">
        <tr>
          <td id="sortable" bind:this={sortable} style="padding: 30px 0;" class:dropArea={isEmpty}>
            {#if isEmpty}
              <span class="col20 f-left ta-center mb-25">{M.DROP_HERE}</span>
            {/if}
            {#each data.content as row, i (row)}
              <Section id={i} bind:data={row} meta={meta} globalStyle={data.style} onFocus={onFocus} onAction={action} isFixed={isFixed} onChange={(e) => onChange(i, e)} />
            {/each}
          </td>
        </tr>
      </table>
    </div>
    <div class="blur-element" bind:this={blurElement} on:click={blur} />
  </div>
</div>

<style>
  :global(.ui-sortable-placeholder) {
    float: left;
    border: 2px solid #4eb3ea;
    width: calc(100% - 4px);
  }

  .editor-wrapper {
    height: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    overflow-y: auto;
  }

  :global(body.rtl-website .editor-wrapper ul,
  body.rtl-website .editor-wrapper ol,
  body.rtl-website .editor-wrapper .table-wrapper,
  body.rtl-website .editor-wrapper table,
  body.rtl-website .editor-wrapper tr,
  body.rtl-website .editor-wrapper td,
  body.rtl-website .editor-wrapper th,
  body.rtl-website .editor-wrapper span,
  body.rtl-website .editor-wrapper p,
  body.rtl-website .editor-wrapper h1,
  body.rtl-website .editor-wrapper h2,
  body.rtl-website .editor-wrapper h3,
  body.rtl-website .editor-wrapper h4,
  body.rtl-website .editor-wrapper h5,
  body.rtl-website .editor-wrapper h6,
  body.rtl-website .editor-wrapper li,
  body.rtl-website .editor-wrapper a) {
    direction: rtl;
  }

  .dropArea {
    text-align: center;
    background-color: #e6e9f0;
    min-width: 600px;
    margin-top: 25px;
    float: left;
    width: 100%;
  }

  .note-editor .note-editing-area .note-editable {
    overflow: hidden;
  }

  .blur-element {
    width: 100%;
    height: 100vh;
    position: absolute;
    top: 150px;
    left: 0;
    z-index: 0;
  }

  .table-wrapper {
    z-index: 1;
  }

  .rel {
    position: relative !important;
  }

  .section {
    margin-bottom: 30px;
    position: relative;
    float: unset;
    z-index: 15;
    max-width: 600px;
  }

  :global(.editor-wrapper .wrapper) {
    color: black;
  }

  :global(.editor-wrapper h1, .editor-wrapper h2, .editor-wrapper h3, .editor-wrapper h4, .editor-wrapper h5) {
    font-weight: 700;
  }

  :global(.row) {
    margin: 0 auto;
  }

  :global(.row .row),
  :global(th .row) {
    width: 100%;
  }

  :global(.column) {
    font-size: 13px;
    line-height: 23px;
  }

  :global(.column-st) {
    padding-left: 40px;
    padding-right: 40px;
    text-align: left;
  }

  :global(.component-center) {
    margin-left: auto !important;
    margin-right: auto !important;
  }

  :global(.component-left) {
    margin-left: 0;
    margin-right: auto;
  }

  :global(.component-right) {
    margin-left: auto;
    margin-right: 0;
  }

  :global(.editor-wrapper div, .editor-wrapper tbody, .editor-wrapper td, .editor-wrapper tr, .editor-wrapper th, .editor-wrapper table) {
    vertical-align: middle !important;
  }

  :global(.editor-wrapper .note-editor) {
    font-family: inherit !important;
  }

  :global(.editor-wrapper .note-editable) {
    color: #000000 !important;
  }
</style>
